/**
 * `spotter` customer-portal config — the "not hardcoded inline for no reason" home for anything
 * that varies per-org/config in this interface (spec ask: "everything that should be configurable
 * should be made so, even if that just means a separate .ts config file for now — not a full admin
 * UI"). Mirrors the mock's `F`/`TEAMS`/`VIEWS`/`GLOSS_FIELDS` prototype stand-ins, but for the real
 * app: the brand wordmark text and breadcrumb copy live here instead of inline in JSX, so a later
 * round can repoint them (or make them per-instance plugin variables) without hunting through
 * component files. Per-org data that's already sourced live (iconik views, the vendored output
 * contract, the customer's org name) is NOT duplicated here — this file is only for the bits that
 * would otherwise be a hardcoded literal with no live source.
 */

/** Brand name — both the header wordmark (`SpotterHeader`, solid white next to the `SpotterMark`
 *  reticle icon — no more two-tone "Spot" + amber "ter"; that split only existed to inject accent
 *  color into a wordmark with no icon, and became redundant once the reticle carried the accent
 *  on its own) and the single source of truth for inline copy ("fed by X", "Copied through
 *  exactly as ___ produces it", "Unused ___ sources", etc.) — round 9: several call sites had
 *  hardcoded "CineSys" (CineSys is the vendor building this tool, not the customer-facing product
 *  name), which read as an unexplained, unbranded term to a customer and was the root of the
 *  "Copied through exactly as CineSys produces it" confusion report. */
export const SPOTTER_WORDMARK = "Spotter";

/** The `requirePlugin(interfaceId)` string `apps/customer-api/src/routes/index.ts` gates every
 *  `/api/spotter/*` route with, and the same id `GET /api/context`'s `enabledInterfaces` carries
 *  — MUST stay in sync (no shared package between the two apps to import it from). Feeds
 *  `useInterfaceAccess`, the single "is this org actually entitled to Spotter" check every
 *  `spotter/*` header/screen uses. */
export const SPOTTER_INTERFACE_ID = "spotter-mapping";

/** Top-level nav tab defs — routes, not local UI state, so `key` is an absolute path. "Fields"
 *  covers Field Mapping + Glossary (see `fields/FieldsScreen.tsx`); "Facial Recognition" is its
 *  own tab (`facialRecognition/FrScreen.tsx`) rather than a Fields mode, since FR-person routing
 *  is stored and saved independently. "Gamebook" see `gamebook/GamebookScreen.tsx`; "File
 *  Rename" see `rename/RenameScreen.tsx`; "Usage" see `usage/UsageScreen.tsx`; "Control Room"
 *  (Monday 12911625908) see `controlRoom/ControlRoomScreen.tsx` — rides the same Spotter gate as
 *  every other tab here (no separate per-org toggle). NO separate "Anchor Review" tab anymore
 *  (Monday 12871494046, consolidation round) — Films Ground/Top Films' review surface now opens
 *  IN PLACE from a Control Room row (`filmsGround/AnchorReviewPanel.tsx`), not its own nav tab. */
/** Named separately (rather than inlined in `SPOTTER_TOP_TABS` below) so `SpotterHeader.tsx` can
 *  key its "needs attention" badge (Addison: "show a little needs attention icon on the control
 *  room tab... even when you're on a different tab?") off the same string this tab's own `key`
 *  uses, without a second hardcoded `"/spotter/control-room"` literal drifting out of sync with
 *  it. */
export const CONTROL_ROOM_TAB_KEY = "/spotter/control-room";

export const SPOTTER_TOP_TABS = [
  { key: "/spotter/fields", label: "Fields" },
  { key: "/spotter/facial-recognition", label: "Facial Recognition" },
  { key: "/spotter/gamebook", label: "Gamebook" },
  { key: "/spotter/rename", label: "File Rename" },
  { key: "/spotter/usage", label: "Usage" },
  { key: CONTROL_ROOM_TAB_KEY, label: "Control Room" },
] as const;
